import { typeCheckConfig, element } from "../../util/index";
import Data from "../../dom/data";
import EventHandler from "../../dom/event-handler";
import Manipulator from "../../dom/manipulator";
import SelectorEngine from "../../dom/selector-engine";

/**
 * ------------------------------------------------------------------------
 * Constants
 * ------------------------------------------------------------------------
 */

const NAME = "clipboard";
const DATA_KEY = "twe.clipboard";
const EVENT_KEY = `.${DATA_KEY}`;

const DEFAULT_OPTIONS = {
  clipboardTarget: null,
};

const OPTIONS_TYPE = {
  clipboardTarget: "null|string",
};

const EVENT_COPY = `copied${EVENT_KEY}`;

/**
 * ------------------------------------------------------------------------
 * Class Definition
 * ------------------------------------------------------------------------
 */

class Clipboard {
  constructor(element, options = {}) {
    this._element = element;
    this._options = options;

    if (this._element) {
      Data.setData(element, DATA_KEY, this);

      this._initCopy = this._initCopy.bind(this);

      this._setup();
    }
  }

  // Getters

  static get NAME() {
    return NAME;
  }

  get options() {
    const config = {
      ...DEFAULT_OPTIONS,
      ...Manipulator.getDataAttributes(this._element),
      ...this._options,
    };

    typeCheckConfig(NAME, config, OPTIONS_TYPE);

    return config;
  }

  get clipboardTarget() {
    return SelectorEngine.findOne(this.options.clipboardTarget);
  }

  get copyText() {
    const clipboardTextExist = this.clipboardTarget.hasAttribute(
      "data-twe-clipboard-text"
    );
    const inputValue = this.clipboardTarget.value;
    const targetText = this.clipboardTarget.textContent;

    if (clipboardTextExist) {
      return this.clipboardTarget.getAttribute("data-twe-clipboard-text");
    }

    if (inputValue) {
      return inputValue;
    }

    return targetText;
  }

  // Public

  dispose() {
    EventHandler.off(this._element, "click", this._initCopy);

    Data.removeData(this._element, DATA_KEY);
    this._element = null;
  }

  // Private
  _setup() {
    EventHandler.on(this._element, "click", this._initCopy);
  }

  _initCopy() {
    const inputToCopy = this._createNewInput();
    document.body.appendChild(inputToCopy);
    this._selectInput(inputToCopy);
    EventHandler.trigger(this._element, EVENT_COPY, {
      copyText: this.copyText,
    });

    inputToCopy.remove();
  }

  _createNewInput() {
    const tag =
      this.clipboardTarget.tagName === "TEXTAREA" ? "textarea" : "input";
    const newInput = element(tag);
    newInput.value = this.copyText;
    Manipulator.addClass(newInput, `-left-[9999px] absolute`);
    return newInput;
  }

  _selectInput(input) {
    input.select();
    input.focus();
    input.setSelectionRange(0, 99999);

    document.execCommand("copy");
  }

  // Static

  static jQueryInterface(config) {
    return this.each(function () {
      let data = Data.getData(this, DATA_KEY);
      const _config = typeof config === "object" && config;

      if (!data) {
        data = new Clipboard(this, _config);
      }

      if (typeof config === "string") {
        if (typeof data[config] === "undefined") {
          throw new TypeError(`No method named "${config}"`);
        }
        data[config](this);
      }
    });
  }

  static getInstance(element) {
    return Data.getData(element, DATA_KEY);
  }

  static getOrCreateInstance(element, config = {}) {
    return (
      this.getInstance(element) ||
      new this(element, typeof config === "object" ? config : null)
    );
  }
}

export default Clipboard;
